<template lang="pug">
aiq-dialog.advance-crawl-configuration(
  :title="$t(`${baseTranslationPath}.title`)"
  :model-value="visible"
  :show-close="true"
  :close-on-click-modal="false"
  :close-on-press-escape="true"
  width="640px"
  append-to-body
  @update:model-value="onVisibleChange"
  @closed="onClosed"
  )
  .advance-crawl-body
    //- Schedule: Options to pull periodically and automatically
    .config-section(v-if="supportSchedule")
      .section-title {{ $t(`${baseTranslationPath}.schedule`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.enabled")
        label {{ $t(`${baseTranslationPath}.enabled`) }}
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.frequency`) }}
        aiq-select(v-model="localConfig.frequency" :disabled="!localConfig.enabled")
          aiq-option(
            v-for="option in frequencyOptions"
            :key="option"
            :label="$t(`${baseTranslationPath}.frequencies.${option}`)"
            :value="option"
            )

    //- Source
    .config-section
      .section-title {{ $t(`${baseTranslationPath}.source`) }}
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.sitemap_url`) }}
        aiq-input(v-model="localConfig.sitemap_url"
                  :placeholder="$t(`${baseTranslationPath}.sitemap_url_placeholder`)"
                  :max-length="500")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.user_agent`) }}
        aiq-input(v-model="localConfig.user_agent"
                  :placeholder="$t(`${baseTranslationPath}.user_agent_placeholder`)"
                  :max-length="500")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.warmup_nav_path`) }}
        aiq-input(v-model="localConfig.warmup_nav_path"
                  :placeholder="$t(`${baseTranslationPath}.warmup_nav_path_placeholder`)"
                  :max-length="500")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.extra_headers`) }}
        .header-row(v-for="(header, index) in extraHeaderRows" :key="index")
          aiq-input.header-key(v-model="header.key"
                              :placeholder="$t(`${baseTranslationPath}.header_key_placeholder`)"
                              @input="syncExtraHeaders")
          aiq-input.header-value(v-model="header.value"
                                :placeholder="$t(`${baseTranslationPath}.header_value_placeholder`)"
                                @input="syncExtraHeaders")
          aiq-button.remove-header(type="text" @click="removeHeaderRow(index)")
            fa-icon(:icon="['fas', 'trash']")
        aiq-button.add-header(:plain="true" @click="addHeaderRow") {{ $t(`${baseTranslationPath}.add_header`) }}

    //- Rules
    .config-section
      .section-title {{ $t(`${baseTranslationPath}.rules`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.rules.use_sitemap")
        label {{ $t(`${baseTranslationPath}.use_sitemap`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.rules.follow_links")
        label {{ $t(`${baseTranslationPath}.follow_links`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.rules.full_resource_loading")
        label {{ $t(`${baseTranslationPath}.full_resource_loading`) }}
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.include_paths`) }}
        aiq-select(v-model="localConfig.rules.include_paths"
                  multiple
                  filterable
                  :allow-create="true"
                  :default-first-option="true"
                  :reserve-keyword="false"
                  :placeholder="$t(`${baseTranslationPath}.paths_placeholder`)")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.exclude_paths`) }}
        aiq-select(v-model="localConfig.rules.exclude_paths"
                  multiple
                  filterable
                  :allow-create="true"
                  :default-first-option="true"
                  :reserve-keyword="false"
                  :placeholder="$t(`${baseTranslationPath}.paths_placeholder`)")
      .number-grid
        .field-container
          label {{ $t(`${baseTranslationPath}.max_pages`) }}
          aiq-input-number(v-model="localConfig.rules.max_pages" :min="1" :controls="false")
        .field-container
          label {{ $t(`${baseTranslationPath}.max_depth`) }}
          aiq-input-number(v-model="localConfig.rules.max_depth" :min="0" :controls="false")
        .field-container
          label {{ $t(`${baseTranslationPath}.page_delay_seconds`) }}
          aiq-input-number(v-model="localConfig.rules.page_delay_seconds" :min="0" :step="0.5" :precision="1" :controls="false")
        .field-container
          label {{ $t(`${baseTranslationPath}.js_render_wait_ms`) }}
          aiq-input-number(v-model="localConfig.rules.js_render_wait_ms" :min="0" :step="100" :controls="false")
        .field-container
          label {{ $t(`${baseTranslationPath}.context_pool_size`) }}
          aiq-input-number(v-model="localConfig.rules.context_pool_size" :min="1" :controls="false")

  template(#footer)
    .dialog-footer
      aiq-button(@click="onCancel") {{ $t('global.cancel') }}
      aiq-button(type="primary" @click="onApply") {{ $t('global.save') }}
</template>
<script>
import cloneDeep from 'lodash/cloneDeep';

const FREQUENCY_OPTIONS = ['hourly', 'daily', 'every_2_days', 'every_3_days', 'weekly'];

export default {
  name: 'AdvanceCrawlConfiguration',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      required: true,
    },
    supportSchedule: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:visible', 'update:config'],
  data() {
    return {
      baseTranslationPath: 'settings_tab.smart_drive_tab.edit.advanced_crawling',
      frequencyOptions: FREQUENCY_OPTIONS,
      localConfig: cloneDeep(this.config),
      extraHeaderRows: [],
    };
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.resetLocalConfig();
      }
    },
  },
  created() {
    this.resetLocalConfig();
  },
  methods: {
    resetLocalConfig() {
      this.localConfig = cloneDeep(this.config);
      this.extraHeaderRows = Object.entries(this.localConfig.extra_headers || {})
        .map(([key, value]) => ({ key, value }));
    },
    addHeaderRow() {
      this.extraHeaderRows.push({ key: '', value: '' });
    },
    removeHeaderRow(index) {
      this.extraHeaderRows.splice(index, 1);
      this.syncExtraHeaders();
    },
    syncExtraHeaders() {
      this.localConfig.extra_headers = this.extraHeaderRows.reduce((acc, { key, value }) => {
        const trimmedKey = (key || '').trim();
        if (trimmedKey) {
          acc[trimmedKey] = value;
        }
        return acc;
      }, {});
    },
    onVisibleChange(value) {
      this.$emit('update:visible', value);
    },
    onClosed() {
      this.$emit('update:visible', false);
    },
    onCancel() {
      this.$emit('update:visible', false);
    },
    onApply() {
      this.syncExtraHeaders();
      this.$emit('update:config', cloneDeep(this.localConfig));
      this.$emit('update:visible', false);
    },
  },
};
</script>
<style lang="scss" scoped>
.advance-crawl-body {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 6px;
}

.config-section {
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #e4e7ed;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
  margin-bottom: 12px;
}

.field-container {
  margin-top: 12px;

  label {
    display: block;
    margin-bottom: 6px;
    color: #475669;
    font-size: 13px;
  }
}

.full-width {
  width: 100%;

  :deep(.el-select) {
    width: 100%;
  }
}

.flex-display {
  display: flex;
  align-items: center;
  width: 100%;

  label {
    margin-bottom: 0;
  }
}

.switch {
  margin-right: 10px;
}

.gap-between {
  gap: 10px;
}

.number-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .header-key {
    flex: 1;
  }

  .header-value {
    flex: 2;
  }
}

.add-header {
  margin-top: 4px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

:deep(.el-switch.is-checked .el-switch__core) {
  background-color: #5993FF !important;
  border-color: #5993FF !important;
}
</style>
